import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useEnglishText } from "../../text/useEnglishText.js";
import { ArrowDown, ArrowUp, ArrowUpDown, ListFilter, X } from "lucide-react";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";
import { extractColumns } from "../../utils/extractColumns";
import "../../styles/DocumentsTable.css";

const getRowIdentity = (row, index) =>
  row?.ID ?? row?.id ?? row?.PARTITION_LICENSES_ID ?? row?.COMPANY_ID ?? row?.SYS_USER_ID ?? row?.TICKET_ID ?? index;

export default function DocumentsTable({
  data = [],
  columnsData = data,
  onSelect,
  showSelection = true,
  filters = {},
  setFilters,
  sort = {},
  setSort,
}) {
  const navigate = useNavigate();
  const { t } = useEnglishText();
  const [selectedKey, setSelectedKey] = useState(null);
  const [openColumn, setOpenColumn] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const filterButtons = useRef({});
  const popoverRef = useRef(null);
  const columns = useMemo(() => extractColumns(columnsData), [columnsData]);
  const hasAnyFilters = Object.values(filters).some(hasFilterValue);

  const handleSelect = (row, index) => {
    setSelectedKey(getRowIdentity(row, index));
    onSelect?.(row);
  };

  const handleDoubleClick = (row) => {
    if (!row?.ID) return;
    navigate(`${row.ID}`);
  };

  const formatCellValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (Array.isArray(value)) {
      return value.map((entry) => (typeof entry === "object" ? JSON.stringify(entry) : entry)).join(", ");
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const changeSort = (column) => {
    if (!setSort) return;
    setSort((current) => ({
      column,
      direction: current?.column === column && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const positionPopover = (columnKey) => {
    const anchor = filterButtons.current[columnKey];
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(300, window.innerWidth - 24);
    const height = Math.min(270, window.innerHeight - 24);
    const left = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12));
    const below = rect.bottom + 8;
    const top = below + height <= window.innerHeight - 12
      ? below
      : Math.max(12, rect.top - height - 8);
    setPopoverPosition({ top, left, width });
  };

  useEffect(() => {
    if (!openColumn) return undefined;
    positionPopover(openColumn);
    const reposition = () => positionPopover(openColumn);
    const onPointerDown = (event) => {
      const anchor = filterButtons.current[openColumn];
      if (popoverRef.current?.contains(event.target) || anchor?.contains(event.target)) return;
      setOpenColumn(null);
      setPopoverPosition(null);
    };
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpenColumn(null);
      setPopoverPosition(null);
      filterButtons.current[openColumn]?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [openColumn]);

  const activeColumn = columns.find((column) => column.key === openColumn);

  return (
    <div className="documents-table-container">
      <TooltipProvider delayDuration={300}>
        <Table aria-label={t("common.dataTable", "Data table")}>
          <TableHeader>
            <TableRow>
              {showSelection && <TableHead className="w-10" aria-label={t("common.selectRecord", "Select record")} />}
              {columns.map((column) => {
                const activeSort = sort?.column === column.key;
                const isFiltered = hasFilterValue(filters[column.key]);
                const sortIcon = !activeSort
                  ? <ArrowUpDown aria-hidden="true" />
                  : sort.direction === "asc" ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />;
                return (
                  <TableHead key={column.key} scope="col" aria-sort={activeSort ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
                    <div className="documents-table-heading">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="documents-sort-button"
                        onClick={() => changeSort(column.key)}
                        disabled={!setSort}
                        aria-label={`${column.label}: ${activeSort ? sort.direction : t("common.sortAsc", "Sort ascending")}`}
                      >
                        {column.label}{sortIcon}
                      </Button>
                      {setFilters && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              ref={(element) => { filterButtons.current[column.key] = element; }}
                              variant="ghost"
                              size="icon"
                              className={`documents-filter-button ${isFiltered ? "is-filtered" : ""}`}
                              aria-label={`${t("common.filterColumn", "Filter column")}: ${column.label}`}
                              aria-pressed={isFiltered}
                              aria-haspopup="dialog"
                              aria-expanded={openColumn === column.key}
                              onClick={(event) => {
                                event.stopPropagation();
                                const nextColumn = openColumn === column.key ? null : column.key;
                                setOpenColumn(nextColumn);
                                setPopoverPosition(null);
                              }}
                            >
                              <ListFilter aria-hidden="true" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{t("common.filterColumn", "Filter column")}</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => {
              const identity = getRowIdentity(row, index);
              const selected = selectedKey === identity;
              return (
                <TableRow
                  key={identity}
                  data-state={selected ? "selected" : undefined}
                  aria-selected={selected}
                  tabIndex={onSelect || row?.ID ? 0 : undefined}
                  onClick={() => handleSelect(row, index)}
                  onDoubleClick={() => handleDoubleClick(row)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === "Enter") {
                      event.preventDefault();
                      if (row?.ID) handleDoubleClick(row);
                      else handleSelect(row, index);
                    } else if (event.key === " ") {
                      event.preventDefault();
                      handleSelect(row, index);
                    }
                  }}
                  className={row?.ID ? "cursor-pointer" : "cursor-default"}
                >
                  {showSelection && (
                    <TableCell className="w-10" onClick={(event) => event.stopPropagation()}>
                      <Checkbox checked={selected} onChange={() => handleSelect(row, index)} aria-label={`${t("common.selectRecord", "Select record")} ${index + 1}`} />
                    </TableCell>
                  )}
                  {columns.map((column) => <TableCell key={column.key}>{formatCellValue(row[column.key])}</TableCell>)}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TooltipProvider>

      {activeColumn && popoverPosition && createPortal(
        <section
          ref={popoverRef}
          className="documents-column-popover"
          style={popoverPosition}
          role="dialog"
          aria-label={`${t("common.filterColumn", "Filter column")}: ${activeColumn.label}`}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <header className="documents-column-popover__header">
            <div>
              <span>{t("common.filterColumn", "Filter column")}</span>
              <strong>{activeColumn.label}</strong>
            </div>
            <Button variant="ghost" size="icon" className="documents-column-popover__close" aria-label={t("common.close", "Close")} title={t("common.close", "Close")} onClick={() => { setOpenColumn(null); setPopoverPosition(null); }}>
              <X aria-hidden="true" />
            </Button>
          </header>
          <label className="documents-column-popover__field">
            <span>{t("common.filter", "Filter")}</span>
            {activeColumn.type === "enum" ? (
              <select
                value={filters[activeColumn.key] ?? ""}
                onChange={(event) => updateColumnFilter(setFilters, activeColumn.key, event.target.value)}
                autoFocus
              >
                <option value="">{t("common.all", "All")}</option>
                {activeColumn.options?.map((option) => <option key={String(option)} value={option}>{String(option)}</option>)}
              </select>
            ) : (
              <Input
                type={activeColumn.type === "date" ? "date" : "text"}
                value={filters[activeColumn.key] ?? ""}
                onChange={(event) => updateColumnFilter(setFilters, activeColumn.key, event.target.value)}
                placeholder={activeColumn.label}
                autoFocus
              />
            )}
          </label>
          <div className="documents-column-popover__actions">
            <Button variant="outline" disabled={!hasFilterValue(filters[activeColumn.key])} onClick={() => updateColumnFilter(setFilters, activeColumn.key, "")}>
              {t("common.clear", "Clear filter")}
            </Button>
            <Button variant="primary" disabled={!hasAnyFilters} onClick={() => setFilters({})}>
              {t("common.clearAll", "Clear all")}
            </Button>
          </div>
        </section>,
        document.body,
      )}
    </div>
  );
}

function hasFilterValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function updateColumnFilter(setFilters, key, value) {
  setFilters((current) => {
    const next = { ...current };
    if (hasFilterValue(value)) next[key] = value;
    else delete next[key];
    return next;
  });
}
